import { getDistance } from '@shared/helpers/geometry/coordinates.helpers';
import {
  computePolygonArea,
  computePolygonPerimeter,
  doPolygonsIntersect,
  getPointOnLine,
  isPointInPolygon,
  isPolygon2D,
  isPolygonContainedInPolygon,
  projectPointOntoSegmentClamped,
} from '@shared/helpers/geometry/geometry.helpers';
import { isCloseTo } from '@shared/helpers/math.helpers';
import { Shape, Storey } from '@shared/models/design/storey.model';
import { Line2D, Point2D, Polygon2D } from '@shared/models/geometry.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  ELEVATION_TOUCH_EPSILON,
  getAllStoreys,
  getBuildingContainingStorey,
  getStoreyElevationRange,
  getStoreyIndex,
  getStoreys,
} from './storey.helpers';
import { designGuards } from './design-typeguards.helpers';
import { BuildingDesign } from '@shared/models/design/building-design.model';

export const CUTOUT_MIN_MARGIN_METERS = 1;

/**
 * Cutout center snaps to its enclosing solid when the cutout centroid is within this distance (m) after the proposed move.
 */
export const CUTOUT_CENTER_SNAP_NEAR_M = 2.5;

/**
 * When farther than {@link CUTOUT_CENTER_SNAP_NEAR_M}, still snap only if the drag moves meaningfully toward the solid center and ends within this distance (m).
 */
export const CUTOUT_CENTER_SNAP_FAR_M = 10;

/**
 * Minimum reduction in centroid-to-centroid distance (m) required for the “far” snap path — avoids snapping on tiny pointer jitter.
 */
export const CUTOUT_CENTER_SNAP_MIN_APPROACH_M = 0.35;

export type CutoutKind = 'atrium' | 'courtyard';

type CutoutCoverAbove = 'shaft' | 'atrium' | 'open';

/**
 * Return all cutout shapes on a storey.
 * @param storey
 */
export const getCutoutShapes = (storey: Storey | undefined): Shape[] =>
  (storey?.shapes ?? []).filter((shape) => shape.cutout === true);

/**
 * Return all non-cutout ("solid") shapes on a storey.
 * @param storey
 */
export const getSolidShapes = (storey: Storey | undefined): Shape[] =>
  (storey?.shapes ?? []).filter((shape) => shape.cutout !== true);

/**
 * Compute area contribution for a storey where cutouts remove floor area.
 * @param storey
 */
export const getStoreyGfaExcludingCutouts = (
  storey: Storey | undefined,
): number => {
  if (!storey) return 0;
  const solidArea = getSolidShapes(storey).reduce(
    (sum, shape) => sum + computePolygonArea(shape.points),
    0,
  );
  const cutoutArea = getCutoutShapes(storey).reduce(
    (sum, shape) => sum + computePolygonArea(shape.points),
    0,
  );
  return Math.max(0, solidArea - cutoutArea);
};

/**
 * Sum all cutout perimeters for the storey.
 * @param storey
 */
export const getStoreyInnerPerimeter = (storey: Storey | undefined): number =>
  getCutoutShapes(storey).reduce(
    (sum, shape) => sum + computePolygonPerimeter(shape.points),
    0,
  );

/**
 * Resolve the closest enclosing non-cutout shape for a cutout candidate.
 * @param storey
 * @param cutout
 */
export const findEnclosingSolidShape = (
  storey: Storey | undefined,
  cutout: Shape,
): Shape | undefined => {
  if (!storey || !isPolygon2D(cutout.points)) return undefined;
  const candidates = getSolidShapes(storey).filter((shape) =>
    isShapeInsideShape(cutout, shape),
  );
  if (!candidates.length) return undefined;

  // Pick the smallest containing polygon so nested solids choose the tight container.
  return candidates.reduce<Shape | undefined>((best, candidate) => {
    if (!best) return candidate;
    const bestArea = computePolygonArea(best.points);
    const candidateArea = computePolygonArea(candidate.points);
    return candidateArea < bestArea ? candidate : best;
  }, undefined);
};

/**
 * Cutouts on {@link storey} whose smallest enclosing solid is {@link solid}.
 * @param storey
 * @param solid Non-cutout shape on the same storey
 */
export const getCutoutsEnclosedBySolid = (
  storey: Storey,
  solid: Shape,
): Shape[] => {
  if (solid.cutout === true) return [];
  return getCutoutShapes(storey).filter(
    (cutout) => findEnclosingSolidShape(storey, cutout)?.id === solid.id,
  );
};

/**
 * Shapes on {@link storey} that lie fully inside {@link cutout}, excluding the
 * cutout itself. Deleting a hole removes these with it — courtyard houses and
 * nested holes (DEV-3843). The enclosing solid is not contained in the hole.
 */
export const getShapesContainedInCutout = (
  storey: Storey,
  cutout: Shape,
): Shape[] => {
  if (cutout.cutout !== true || !isPolygon2D(cutout.points)) return [];
  return storey.shapes.filter(
    (shape) =>
      shape.id !== cutout.id &&
      isPolygon2D(shape.points) &&
      isPolygonContainedInPolygon(shape.points, cutout.points),
  );
};

/**
 * Smallest cutout on {@link storey} that fully contains {@link solid}.
 * Courtyard houses are dragged inside this hole, not the outer ring.
 */
export const findSmallestContainingCutout = (
  storey: Storey | undefined,
  solid: Shape,
): Shape | undefined => {
  if (!storey || solid.cutout === true || !isPolygon2D(solid.points)) {
    return undefined;
  }
  const containers = getCutoutShapes(storey).filter(
    (cutout) =>
      isPolygon2D(cutout.points) &&
      isPolygonContainedInPolygon(solid.points, cutout.points),
  );
  return containers.reduce<Shape | undefined>((best, candidate) => {
    if (!best) return candidate;
    return computePolygonArea(candidate.points) <
      computePolygonArea(best.points)
      ? candidate
      : best;
  }, undefined);
};

/**
 * Storey where {@link solid} is a cutout and {@link cutout} is its enclosing solid.
 * Reuses cutout margin clamping so a courtyard house stays inside the hole.
 */
const asCutoutBoundedByHole = (
  storey: Storey,
  solid: Shape,
  cutout: Shape,
): { storey: Storey; shape: Shape } => {
  const shape: Shape = { ...solid, cutout: true };
  const boundary: Shape = { ...cutout, cutout: false };
  return {
    shape,
    storey: { ...storey, shapes: [boundary, shape] },
  };
};

/**
 * Keep a courtyard-house translation inside its cutout, with the same margin a
 * cutout keeps from its enclosing solid. Solids that are not inside a cutout
 * are unchanged.
 */
export const clampSolidTranslationInsideCutout = (
  storey: Storey | undefined,
  solid: Shape,
  delta: Point2D,
  minMarginMeters: number = CUTOUT_MIN_MARGIN_METERS,
): Point2D => {
  if (!storey || solid.cutout === true) return delta;
  const cutout = findSmallestContainingCutout(storey, solid);
  if (!cutout) return delta;
  const bounded = asCutoutBoundedByHole(storey, solid, cutout);
  return clampCutoutTranslationToMargin(
    bounded.storey,
    bounded.shape,
    delta,
    minMarginMeters,
  );
};

/**
 * Clamp one vertex of a courtyard house so the footprint stays inside its cutout.
 * Solids that are not inside a cutout return {@link targetVertex}.
 */
export const resolveClampedSolidVertexInsideCutout = ({
  storey,
  solid,
  vertexIndex,
  currentVertex,
  targetVertex,
  minMarginMeters = CUTOUT_MIN_MARGIN_METERS,
}: {
  storey: Storey | undefined;
  solid: Shape;
  vertexIndex: number;
  currentVertex: Point2D;
  targetVertex: Point2D;
  minMarginMeters?: number;
}): Point2D => {
  if (!storey || solid.cutout === true) return targetVertex;
  const cutout = findSmallestContainingCutout(storey, solid);
  if (!cutout) return targetVertex;
  const bounded = asCutoutBoundedByHole(storey, solid, cutout);
  return resolveClampedCutoutVertexPosition({
    storey: bounded.storey,
    cutout: bounded.shape,
    vertexIndex,
    currentVertex,
    targetVertex,
    minMarginMeters,
  });
};

/**
 * Validate that a cutout shape is fully enclosed and keeps an inner margin.
 * @param cutout
 * @param storey
 * @param minMarginMeters
 */
export const isCutoutInsideStoreyWithMargin = ({
  cutout,
  storey,
  minMarginMeters = CUTOUT_MIN_MARGIN_METERS,
}: {
  cutout: Shape;
  storey: Storey | undefined;
  minMarginMeters?: number;
}): boolean => {
  if (!storey || !isPolygon2D(cutout.points)) return false;
  const enclosingShape = findEnclosingSolidShape(storey, cutout);
  if (!enclosingShape) return false;
  return getMinDistanceToShapeEdges(cutout, enclosingShape) >= minMarginMeters;
};

/**
 * Fast path: only skip boundary refinements when direct motion toward the pointer is
 * clearly working (mezzanine uses a similar gate before slide).
 */
const CUTOUT_VERTEX_DRAG_DIRECT_GATE_M = 0.02;

/**
 * When dragging a cutout vertex, binary-search the furthest valid position on the segment
 * current → target (same pattern as mezzanine furthest-valid drag ray in snap).
 */
const findFurthestValidCutoutVertexOnRay = (
  current: Point2D,
  target: Point2D,
  isValidVertexPosition: (vertex: Point2D) => boolean,
): Point2D => {
  const dx = target[0] - current[0];
  const dy = target[1] - current[1];
  if (dx * dx + dy * dy < 1e-12) return current;

  const at = (t: number): Point2D => [current[0] + dx * t, current[1] + dy * t];
  if (!isValidVertexPosition(at(0))) return current;
  if (isValidVertexPosition(at(1))) return target;

  let low = 0;
  let high = 1;
  for (let step = 0; step < 18; step += 1) {
    const mid = (low + high) / 2;
    if (isValidVertexPosition(at(mid))) low = mid;
    else high = mid;
  }
  return at(low);
};

const getSolidPolygonEdges = (points: Point2D[]): Line2D[] => {
  const edges: Line2D[] = [];
  const total = points.length;
  if (total < 2) return edges;
  for (let i = 0; i < total; i += 1) {
    const start = points[i];
    const end = points[(i + 1) % total];
    if (!start || !end) continue;
    edges.push([start, end]);
  }
  return edges;
};

/**
 * Project drag onto unit directions (enclosing walls or cutout edges) and take the best
 * valid slide. Unlike the old 0.02 m floor, any sub-millimeter step still tracks continuous
 * drags once the vertex sits on the margin boundary.
 */
const slideCutoutVertexAlongUnitDirections = (
  current: Point2D,
  target: Point2D,
  unitDirections: Point2D[],
  isValidVertexPosition: (vertex: Point2D) => boolean,
): Point2D | undefined => {
  const dragDx = target[0] - current[0];
  const dragDy = target[1] - current[1];
  const dragLength = Math.hypot(dragDx, dragDy);
  if (dragLength < 1e-9) return undefined;

  let best: Point2D | undefined;
  let bestDistance = 0;

  for (const [ux, uy] of unitDirections) {
    const dot = dragDx * ux + dragDy * uy;
    if (Math.abs(dot) < 1e-12) continue;

    const slideTarget: Point2D = [current[0] + ux * dot, current[1] + uy * dot];
    const candidate = findFurthestValidCutoutVertexOnRay(
      current,
      slideTarget,
      isValidVertexPosition,
    );
    const dist = getDistance(current, candidate);
    if (dist > bestDistance) {
      bestDistance = dist;
      best = candidate;
    }
  }

  if (best && bestDistance > 1e-9) return best;
  return undefined;
};

/**
 * Mezzanine-style pull: for each enclosing edge, move as far as possible toward the
 * pointer’s projection onto that edge (helps when the direct ray is blocked but the cursor
 * sits near a wall segment).
 */
const bestPullTowardEnclosingEdges = (
  current: Point2D,
  target: Point2D,
  enclosingEdges: Line2D[],
  isValidVertexPosition: (vertex: Point2D) => boolean,
): Point2D | undefined => {
  if (!enclosingEdges.length) return undefined;

  const maxStep = getDistance(current, target) + 0.5;
  let best: Point2D | undefined;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const [edgeStart, edgeEnd] of enclosingEdges) {
    const projected = projectPointOntoSegmentClamped(
      target,
      edgeStart,
      edgeEnd,
    );
    const candidate = findFurthestValidCutoutVertexOnRay(
      current,
      projected,
      isValidVertexPosition,
    );
    const stepDistance = getDistance(current, candidate);
    if (stepDistance > maxStep) continue;

    const score = getDistance(target, candidate) + stepDistance * 0.15;
    if (score >= bestScore) continue;
    bestScore = score;
    best = candidate;
  }

  return best;
};

/**
 * Prefer the candidate closest to the pointer; on equal distance prefer more motion from
 * the anchor vertex so corner drags stay responsive instead of locking to the first tie.
 */
const pickClosestToTargetWithTieBreak = (
  candidates: Point2D[],
  target: Point2D,
  anchor: Point2D,
): Point2D =>
  candidates.reduce((chosen, p) => {
    const distChosen = getDistance(chosen, target);
    const distP = getDistance(p, target);
    if (distP < distChosen - 1e-9) return p;
    if (distP > distChosen + 1e-9) return chosen;
    return getDistance(p, anchor) > getDistance(chosen, anchor) ? p : chosen;
  });

/**
 * Sweep directions in a 180° fan around the pointer vector. Constraint intersections often
 * allow motion along a tangent that is not aligned with any single polygon edge, which
 * leaves pure edge-slides and the direct ray stuck at t ≈ 0.
 */
const searchSectorForBestCutoutVertexTowardTarget = (
  current: Point2D,
  target: Point2D,
  isValidVertexPosition: (vertex: Point2D) => boolean,
): Point2D => {
  const dx = target[0] - current[0];
  const dy = target[1] - current[1];
  const pointerDist = Math.hypot(dx, dy);
  // Minimum reach so we still probe when the cursor sits almost on the vertex
  const maxDist = Math.max(pointerDist, 0.5);
  if (maxDist < 1e-9) return current;

  const baseAngle = Math.atan2(dy, dx);
  const samples = 40;
  const halfSpan = Math.PI / 2;

  let best = current;
  let bestDistToTarget = getDistance(current, target);

  for (let i = 0; i < samples; i += 1) {
    const fraction = samples > 1 ? i / (samples - 1) : 0.5;
    const angle = baseAngle - halfSpan + fraction * (2 * halfSpan);
    const far: Point2D = [
      current[0] + Math.cos(angle) * maxDist,
      current[1] + Math.sin(angle) * maxDist,
    ];
    const candidate = findFurthestValidCutoutVertexOnRay(
      current,
      far,
      isValidVertexPosition,
    );
    const dtt = getDistance(candidate, target);
    if (dtt < bestDistToTarget - 1e-9) {
      bestDistToTarget = dtt;
      best = candidate;
    } else if (Math.abs(dtt - bestDistToTarget) <= 1e-9) {
      if (getDistance(candidate, current) > getDistance(best, current))
        best = candidate;
    }
  }

  return best;
};

const buildCutoutVertexPositionValidator = ({
  storey,
  cutout,
  vertexIndex,
  minMarginMeters,
}: {
  storey: Storey;
  cutout: Shape;
  vertexIndex: number;
  minMarginMeters: number;
}): ((vertex: Point2D) => boolean) => {
  return (vertex: Point2D) => {
    const nextShape: Shape = {
      ...cutout,
      points: cutout.points.map((point, index) =>
        index === vertexIndex ? vertex : point,
      ) as Polygon2D,
    };
    const nextStorey: Storey = {
      ...storey,
      shapes: storey.shapes.map((candidate) =>
        candidate.id === cutout.id ? nextShape : candidate,
      ),
    };
    return isCutoutInsideStoreyWithMargin({
      cutout: nextShape,
      storey: nextStorey,
      minMarginMeters,
    });
  };
};

/**
 * Resolve a cutout vertex position that follows the pointer as far as margin allows,
 * including sliding along the enclosing solid when the direct segment is blocked
 * (same idea as mezzanine boundary handling in snap).
 *
 * @param storey Storey containing the cutout
 * @param cutout Cutout shape being edited
 * @param vertexIndex Index of the dragged vertex
 * @param currentVertex Last committed vertex position (metric space)
 * @param targetVertex Pointer target in metric space
 * @param minMarginMeters Minimum clearance to enclosing solid edges
 * @returns Clamped vertex position satisfying {@link isCutoutInsideStoreyWithMargin}
 */
export const resolveClampedCutoutVertexPosition = ({
  storey,
  cutout,
  vertexIndex,
  currentVertex,
  targetVertex,
  minMarginMeters = CUTOUT_MIN_MARGIN_METERS,
}: {
  storey: Storey;
  cutout: Shape;
  vertexIndex: number;
  currentVertex: Point2D;
  targetVertex: Point2D;
  minMarginMeters?: number;
}): Point2D => {
  const isValidVertexPosition = buildCutoutVertexPositionValidator({
    storey,
    cutout,
    vertexIndex,
    minMarginMeters,
  });

  const alongPointer = findFurthestValidCutoutVertexOnRay(
    currentVertex,
    targetVertex,
    isValidVertexPosition,
  );

  if (
    getDistance(alongPointer, currentVertex) > CUTOUT_VERTEX_DRAG_DIRECT_GATE_M
  ) {
    return alongPointer;
  }

  const enclosing = findEnclosingSolidShape(storey, cutout);
  const enclosingPoints = enclosing?.points ?? [];
  const enclosingEdges = getSolidPolygonEdges(enclosingPoints);

  const enclosingDirections: Point2D[] = [];
  for (const [a, b] of enclosingEdges) {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;
    enclosingDirections.push([dx / len, dy / len]);
  }

  const total = cutout.points.length;
  const prev = cutout.points[(vertexIndex - 1 + total) % total];
  const next = cutout.points[(vertexIndex + 1) % total];
  const cutoutDirections: Point2D[] = [];
  if (prev) {
    const dx = currentVertex[0] - prev[0];
    const dy = currentVertex[1] - prev[1];
    const len = Math.hypot(dx, dy);
    if (len > 1e-9) cutoutDirections.push([dx / len, dy / len]);
  }
  if (next) {
    const dx = next[0] - currentVertex[0];
    const dy = next[1] - currentVertex[1];
    const len = Math.hypot(dx, dy);
    if (len > 1e-9) cutoutDirections.push([dx / len, dy / len]);
  }

  const slidAlongEnclosing = slideCutoutVertexAlongUnitDirections(
    currentVertex,
    targetVertex,
    enclosingDirections,
    isValidVertexPosition,
  );
  const slidAlongCutout = slideCutoutVertexAlongUnitDirections(
    currentVertex,
    targetVertex,
    cutoutDirections,
    isValidVertexPosition,
  );
  const pulled = enclosingEdges.length
    ? bestPullTowardEnclosingEdges(
        currentVertex,
        targetVertex,
        enclosingEdges,
        isValidVertexPosition,
      )
    : undefined;

  const sector = searchSectorForBestCutoutVertexTowardTarget(
    currentVertex,
    targetVertex,
    isValidVertexPosition,
  );

  const candidates: Point2D[] = [alongPointer, sector];
  if (slidAlongEnclosing) candidates.push(slidAlongEnclosing);
  if (slidAlongCutout) candidates.push(slidAlongCutout);
  if (pulled) candidates.push(pulled);

  return pickClosestToTargetWithTieBreak(
    candidates,
    targetVertex,
    currentVertex,
  );
};

/**
 * How {@link aboveStorey} covers {@link cutout}: a matching void continues the
 * open-sky chain, a containing solid makes an atrium, otherwise the chain ends.
 */
const getCutoutCoverFromStoreyAbove = (
  cutout: Shape,
  aboveStorey: Storey,
): CutoutCoverAbove => {
  if (!isPolygon2D(cutout.points)) return 'open';

  const cutouts = getCutoutShapes(aboveStorey);
  // An overlapping cutout above continues the open-sky chain regardless of shape match.
  if (
    cutouts.some(
      (shape) =>
        isPolygon2D(shape.points) &&
        doPolygonsIntersect(cutout.points, shape.points),
    )
  ) {
    return 'shaft';
  }

  if (
    getSolidShapes(aboveStorey).some((shape) =>
      isShapeInsideShape(cutout, shape),
    )
  ) {
    return 'atrium';
  }

  return 'open';
};

/**
 * True when another building sits on {@link roofElevation} and covers
 * {@link cutout} with a solid. Same-building floors are left to the in-stack walk.
 */
const isCutoutCoveredByOtherBuilding = (
  design: BuildingDesign,
  cutout: Shape,
  roofElevation: number,
  ownStoreyIds: ReadonlySet<string>,
): boolean =>
  getAllStoreys(design).some((other) => {
    if (ownStoreyIds.has(other.id)) return false;
    if (designGuards.is.mezzanineStorey(other)) return false;
    if (
      !isCloseTo(
        getStoreyElevationRange(design, other).bottom,
        roofElevation,
        ELEVATION_TOUCH_EPSILON,
      )
    ) {
      return false;
    }
    return getCutoutCoverFromStoreyAbove(cutout, other) === 'atrium';
  });

/**
 * Classify a cutout as atrium (covered above) or courtyard (open to sky).
 * A cutout is courtyard when there is an unbroken chain of overlapping cutouts
 * on every storey above up to and including the top storey. A covering parent
 * building that sits on the open roof counts as a storey above. If any of those
 * storeys covers the cutout with a solid without a matching void, it is atrium.
 * Same-elevation sibling buildings are not storeys above.
 * @param root
 * @param storeyOrId
 * @param cutout
 */
export const classifyCutoutKind = (
  root: BuildingDesign | Storey[],
  storeyOrId: Storey | Storey['id'],
  cutout: Shape,
): CutoutKind => {
  if (cutout.cutout !== true) return 'courtyard';
  if (!isPolygon2D(cutout.points)) return 'courtyard';
  const storeyId = guards.is.string(storeyOrId) ? storeyOrId : storeyOrId.id;
  const storeys = designGuards.is.buildingDesign(root)
    ? (getBuildingContainingStorey(root, storeyId)?.storeys ?? [])
    : getStoreys(root);
  const baseIndex = getStoreyIndex(storeys, storeyOrId);
  if (baseIndex < 0) return 'courtyard';
  const currentStorey = storeys[baseIndex];
  if (!currentStorey) return 'courtyard';

  const design = designGuards.is.buildingDesign(root) ? root : undefined;
  const ownStoreyIds = new Set(storeys.map((storey) => storey.id));
  const roofOf = (storey: Storey): number =>
    getStoreyElevationRange(storeys, storey).top;

  let chainTop = roofOf(currentStorey);
  if (
    design &&
    isCutoutCoveredByOtherBuilding(design, cutout, chainTop, ownStoreyIds)
  ) {
    return 'atrium';
  }

  // Once the same-building chain breaks, remaining floors above are ignored.
  for (let index = baseIndex + 1; index < storeys.length; index += 1) {
    const aboveStorey = storeys[index];
    if (!aboveStorey) break;

    const cover = getCutoutCoverFromStoreyAbove(cutout, aboveStorey);
    if (cover === 'shaft') {
      chainTop = roofOf(aboveStorey);
      if (
        design &&
        isCutoutCoveredByOtherBuilding(design, cutout, chainTop, ownStoreyIds)
      ) {
        return 'atrium';
      }
      continue;
    }
    if (cover === 'atrium') return 'atrium';
    break;
  }

  return 'courtyard';
};

/**
 * Compute shape center by averaging polygon vertices.
 * @param shape
 */
export const getShapeCenter = (shape: Shape): Point2D => {
  if (!shape.points.length) return [0, 0];
  const [sumX, sumY] = shape.points.reduce<[number, number]>(
    (acc, [x, y]) => [acc[0] + x, acc[1] + y],
    [0, 0],
  );
  const count = shape.points.length;
  return [sumX / count, sumY / count];
};

/**
 * True when translating {@link cutout} by {@link delta} keeps margin to the enclosing solid.
 * @param storey Storey containing the cutout
 * @param cutout Cutout shape
 * @param delta Translation in metres
 * @param minMarginMeters Minimum inset from enclosing edges
 */
export const isCutoutTranslationWithinMargin = (
  storey: Storey,
  cutout: Shape,
  delta: Point2D,
  minMarginMeters: number = CUTOUT_MIN_MARGIN_METERS,
): boolean => {
  if (cutout.cutout !== true || !isPolygon2D(cutout.points)) return true;
  const [dx, dy] = delta;
  const translatedShape: Shape = {
    ...cutout,
    points: cutout.points.map(
      ([x, y]) => [x + dx, y + dy] as Point2D,
    ) as Polygon2D,
  };
  const translatedStorey: Storey = {
    ...storey,
    shapes: storey.shapes.map((candidate) =>
      candidate.id === cutout.id ? translatedShape : candidate,
    ),
  };
  return isCutoutInsideStoreyWithMargin({
    cutout: translatedShape,
    storey: translatedStorey,
    minMarginMeters,
  });
};

/**
 * Largest scalar in [0, 1] such that {@link delta} * t keeps the cutout within margin; returns {@link delta} when already valid.
 * @param storey Storey containing the cutout
 * @param cutout Cutout shape
 * @param delta Desired translation
 * @param minMarginMeters Minimum inset from enclosing edges
 */
export const clampCutoutTranslationToMargin = (
  storey: Storey,
  cutout: Shape,
  delta: Point2D,
  minMarginMeters: number = CUTOUT_MIN_MARGIN_METERS,
): Point2D => {
  if (cutout.cutout !== true) return delta;
  const [dx, dy] = delta;
  if (dx * dx + dy * dy < 1e-18) return delta;
  if (isCutoutTranslationWithinMargin(storey, cutout, delta, minMarginMeters)) {
    return delta;
  }

  let low = 0;
  let high = 1;
  for (let step = 0; step < 20; step += 1) {
    const mid = (low + high) / 2;
    if (
      isCutoutTranslationWithinMargin(
        storey,
        cutout,
        [dx * mid, dy * mid],
        minMarginMeters,
      )
    ) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return [dx * low, dy * low];
};

/**
 * Translation that aligns the cutout centroid with the smallest enclosing solid’s centroid, or `undefined` when magnetic snap should not apply.
 * @param storey Storey containing the cutout
 * @param cutout Cutout shape
 * @param proposedDelta Pointer translation after other clamps (gates snap distance only)
 */
export const resolveCutoutCenterSnapTranslationDelta = (
  storey: Storey | undefined,
  cutout: Shape,
  proposedDelta: Point2D,
): Point2D | undefined => {
  if (!storey || cutout.cutout !== true || !isPolygon2D(cutout.points)) {
    return undefined;
  }

  const enclosingShape = findEnclosingSolidShape(storey, cutout);
  if (!enclosingShape) return undefined;

  const [dx, dy] = proposedDelta;
  const shapeCenter = getShapeCenter(cutout);
  const enclosingCenter = getShapeCenter(enclosingShape);
  const translatedCenter: Point2D = [shapeCenter[0] + dx, shapeCenter[1] + dy];

  const distAfter = getDistance(translatedCenter, enclosingCenter);
  const distBefore = getDistance(shapeCenter, enclosingCenter);

  const withinNear = distAfter <= CUTOUT_CENTER_SNAP_NEAR_M;
  const approachM = distBefore - distAfter;
  const movingCloser = approachM > 1e-9;
  const withinFar = distAfter <= CUTOUT_CENTER_SNAP_FAR_M;
  const meaningfulApproach = approachM >= CUTOUT_CENTER_SNAP_MIN_APPROACH_M;
  // Near zone: snap when already aligned; far zone: only after a deliberate move toward center.
  if (!withinNear && !(movingCloser && meaningfulApproach && withinFar)) {
    return undefined;
  }

  return [
    enclosingCenter[0] - shapeCenter[0],
    enclosingCenter[1] - shapeCenter[1],
  ];
};

const getMinDistanceToShapeEdges = (inner: Shape, outer: Shape): number => {
  const sampleFractions = [0.25, 0.5, 0.75] as const;
  let minDistance = Number.POSITIVE_INFINITY;

  for (let index = 0; index < inner.points.length; index += 1) {
    const start = inner.points[index];
    const end = inner.points[(index + 1) % inner.points.length];
    if (!start || !end) continue;

    const samples: Point2D[] = [
      start,
      ...sampleFractions.map((t) => getPointOnLine([start, end], t)),
    ];
    for (const sample of samples) {
      const edgeDistance = getDistanceToPolygonEdges(sample, outer.points);
      if (edgeDistance < minDistance) minDistance = edgeDistance;
    }
  }

  return minDistance;
};

const getDistanceToPolygonEdges = (
  point: Point2D,
  polygon: Point2D[],
): number => {
  let minDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < polygon.length; index += 1) {
    const start = polygon[index];
    const end = polygon[(index + 1) % polygon.length];
    if (!start || !end) continue;
    const projected = projectPointOntoSegmentClamped(point, start, end);
    const edgeDistance = getDistance(point, projected);
    if (edgeDistance < minDistance) minDistance = edgeDistance;
  }
  return minDistance;
};

const isShapeInsideShape = (inner: Shape, outer: Shape): boolean => {
  if (!isPolygon2D(inner.points) || !isPolygon2D(outer.points)) return false;

  const sampleFractions = [0.25, 0.5, 0.75] as const;
  const allVerticesInside = inner.points.every((point) =>
    isPointInPolygon(point, outer.points),
  );
  if (!allVerticesInside) return false;

  for (let index = 0; index < inner.points.length; index += 1) {
    const start = inner.points[index];
    const end = inner.points[(index + 1) % inner.points.length];
    if (!start || !end) return false;
    const segmentSamples = sampleFractions.map((t) =>
      getPointOnLine([start, end], t),
    );
    const inside = segmentSamples.every((point) =>
      isPointInPolygon(point, outer.points),
    );
    if (!inside) return false;
  }

  return true;
};
